import React, { useState, useCallback } from 'react';
import { Editor, Element, Transforms } from 'slate';
import {
  ReactEditor,
  RenderElementProps,
  useSelected,
  useSlateStatic,
  useReadOnly,
} from 'slate-react';
import { icons, SelectInput } from '@ublend-npm/aulaui-next';
import { BlockToolbar } from '../BlockToolbar/BlockToolbar';

import {
  Container,
  Pre,
  StyledCode,
  SelectContainer,
  SyntaxLabel,
  SyntaxName,
} from './Code.styled';

const languages = [
  { key: 'clike', value: 'C' },
  { key: 'css', value: 'CSS' },
  { key: 'html', value: 'HTML' },
  { key: 'javascript', value: 'JavaScript' },
  { key: 'markup', value: 'Markup' },
  { key: 'mathml', value: 'MathML' },
  { key: 'rss', value: 'RSS' },
  { key: 'svg', value: 'SVG' },
  { key: 'xml', value: 'XML' },
];

type SelectInputChangeEvent = {
  target: { value: string };
};

export type CodeProps = {
  children: RenderElementProps['children'];
  element: Element;
};

export const Code = ({ children, element }: CodeProps) => {
  const isReadOnly = useReadOnly();
  const [isHovering, setHovering] = useState(false);
  const editor = useSlateStatic();
  const isSelected = useSelected();

  const { language } = element;

  const toolbarItems = [
    {
      title: 'Delete',
      isVisible: !isReadOnly,
      icon: icons.Delete,
      onAction: () => {
        const at = ReactEditor.findPath(editor, element);
        Transforms.removeNodes(editor, { at });
        if (editor.children.length === 0) {
          Transforms.insertNodes(editor, [
            {
              children: [{ text: '' }],
            },
          ]);
        }
        ReactEditor.focus(editor);
      },
    },
  ];

  const hoverToolbar = (
    <BlockToolbar isVisible={isSelected || isHovering} items={toolbarItems} />
  );

  const handleChangeSyntax = useCallback(
    ({ target: { value } }: SelectInputChangeEvent) => {
      const { selection: savedSelection } = editor;
      const path = ReactEditor.findPath(editor, element);

      // Set the requested language
      Transforms.setNodes(
        editor,
        { language: value },
        { mode: 'highest', at: path },
      );

      // Slate only triggers decoration when the text is changed. We need every line to
      // be redecorated when the language changes so we reinsert the text.
      // JSON stringify/parse is used as object instances need to be changed so that Slate
      // sees this as new content.
      const fragment = JSON.parse(
        JSON.stringify(Editor.fragment(editor, path)),
      );
      Transforms.delete(editor, { at: path, unit: 'block' });
      Transforms.insertNodes(editor, fragment, {
        mode: 'highest',
        at: path,
      });

      if (savedSelection) {
        editor.selection = savedSelection;
      }

      setTimeout(() => {
        ReactEditor.focus(editor);
      }, 100);
    },
    [editor, element],
  );

  const selectedSyntax = languages.find(({ key }) => key === language);
  const syntaxLabel = selectedSyntax && (
    <SyntaxLabel>
      Syntax: <SyntaxName>{selectedSyntax.value}</SyntaxName>
    </SyntaxLabel>
  );

  return (
    <Container
      data-testid="code-block"
      isSelected={isSelected}
      aria-selected={isSelected}
      isReadOnly={isReadOnly}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {hoverToolbar}
      {isReadOnly ? (
        syntaxLabel
      ) : (
        <SelectContainer contentEditable={false}>
          <SelectInput
            name="syntax"
            label="Syntax"
            tabbable={isSelected}
            data={languages}
            value={language}
            handleChange={handleChangeSyntax}
          />
        </SelectContainer>
      )}
      <Pre>
        <StyledCode className={`language-${language}`}>{children}</StyledCode>
      </Pre>
    </Container>
  );
};
